import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { ChevronDown, MessageSquare } from "lucide-react";
import { useLiveChat } from "../../contexts/LiveChatContext";
import MessageItem from "./MessageItem";
import ThemeLoader from "../ui/ThemeLoader";

const SCROLL_BOTTOM_THRESHOLD = 100;

/**
 * MessageList — squared WhatsApp-style.
 * Exposes scrollToMessage(messageId) via ref for quoted-reply navigation.
 * Loads older messages when scrolled to the top (pagination).
 */
const MessageList = forwardRef(
  ({ downloadingMedia, onDownloadMedia, messagesContainerRef, onStartReply }, ref) => {
    const {
      messages,
      messagesLoading,
      messagesLoadingMore,
      hasMoreMessages,
      loadMoreMessages,
    } = useLiveChat();

    const itemRefs = useRef(new Map());
    const [highlightedId, setHighlightedId] = useState(null);
    const [flashingBarFor, setFlashingBarFor] = useState(null);
    const [showJumpToBottom, setShowJumpToBottom] = useState(false);
    const scrollLoadLockRef = useRef(false);

    const scrollToMessage = useCallback((messageId) => {
      if (!messageId) return false;
      const el = itemRefs.current.get(messageId);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setHighlightedId(messageId);
        window.setTimeout(() => {
          setHighlightedId((cur) => (cur === messageId ? null : cur));
        }, 1400);
        return true;
      }
      return false;
    }, []);

    const scrollToBottom = useCallback((smooth = true) => {
      const el = messagesContainerRef?.current;
      if (!el) return;
      el.scrollTo({
        top: el.scrollHeight,
        behavior: smooth ? "smooth" : "auto",
      });
    }, [messagesContainerRef]);

    useImperativeHandle(
      ref,
      () => ({ scrollToMessage, scrollToBottom }),
      [scrollToMessage, scrollToBottom]
    );

    const updateScrollAffordances = useCallback(() => {
      const el = messagesContainerRef?.current;
      if (!el || messagesLoading || messages.length === 0) {
        setShowJumpToBottom(false);
        return;
      }
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      setShowJumpToBottom(distanceFromBottom > SCROLL_BOTTOM_THRESHOLD);
    }, [messagesContainerRef, messagesLoading, messages.length]);

    const handleReplySnippetClick = useCallback(
      (parentMessageId, clickedMessageId) => {
        const ok = scrollToMessage(parentMessageId);
        if (!ok && clickedMessageId) {
          setFlashingBarFor(clickedMessageId);
          window.setTimeout(() => {
            setFlashingBarFor((cur) => (cur === clickedMessageId ? null : cur));
          }, 1200);
        }
      },
      [scrollToMessage]
    );

    const handleScroll = useCallback(async () => {
      const el = messagesContainerRef?.current;
      updateScrollAffordances();
      if (!el || messagesLoading || messagesLoadingMore || !hasMoreMessages) return;
      if (el.scrollTop > 80) return;
      if (scrollLoadLockRef.current) return;

      scrollLoadLockRef.current = true;
      const prevHeight = el.scrollHeight;
      const prevTop = el.scrollTop;

      try {
        const loaded = await loadMoreMessages();
        if (loaded) {
          requestAnimationFrame(() => {
            const container = messagesContainerRef?.current;
            if (!container) return;
            container.scrollTop = prevTop + (container.scrollHeight - prevHeight);
            updateScrollAffordances();
          });
        }
      } finally {
        window.setTimeout(() => {
          scrollLoadLockRef.current = false;
        }, 400);
      }
    }, [
      messagesContainerRef,
      messagesLoading,
      messagesLoadingMore,
      hasMoreMessages,
      loadMoreMessages,
      updateScrollAffordances,
    ]);

    useEffect(() => {
      updateScrollAffordances();
    }, [messages.length, messagesLoading, updateScrollAffordances]);

    return (
      <div className="ml-root-wrap">
        <div
          ref={messagesContainerRef}
          className="ml-root"
          onScroll={handleScroll}
        >
          <div className="ml-inner">
            {messagesLoading ? (
              <div className="ml-state-center">
                <ThemeLoader
                  size="sm"
                  message="Loading messages…"
                  padded={false}
                  className="bg-transparent"
                />
              </div>
            ) : messages.length === 0 ? (
              <div className="ml-state-center">
                <div className="ml-empty-icon">
                  <MessageSquare size={32} strokeWidth={1.2} />
                </div>
                <p className="ml-empty-label">No messages yet</p>
                <p className="ml-empty-sub">Start the conversation by sending a message below.</p>
              </div>
            ) : (
              <>
                {messagesLoadingMore && (
                  <div className="ml-load-more">
                    <ThemeLoader
                      size="xs"
                      spinnerOnly
                      padded={false}
                      className="bg-transparent"
                    />
                    <span className="ml-load-more__label">Loading older messages…</span>
                  </div>
                )}
                {!messagesLoadingMore && hasMoreMessages && (
                  <p className="ml-scroll-hint">Scroll up for older messages</p>
                )}
                {messages.map((message) => {
                  const key = message.id || message.message_id;
                  const msgId = message.message_id;
                  return (
                    <div
                      key={key}
                      ref={(el) => {
                        if (!msgId) return;
                        el ? itemRefs.current.set(msgId, el) : itemRefs.current.delete(msgId);
                      }}
                      className={`ml-msg-wrapper${highlightedId === msgId ? " ml-msg-wrapper--highlight" : ""}`}
                    >
                      <MessageItem
                        message={message}
                        downloadingMedia={downloadingMedia}
                        onDownloadMedia={onDownloadMedia}
                        onStartReply={onStartReply}
                        onReplySnippetClick={(parentId) =>
                          handleReplySnippetClick(parentId, msgId)
                        }
                        isHighlighted={highlightedId === msgId}
                        flashQuotedBar={flashingBarFor === msgId}
                      />
                    </div>
                  );
                })}
              </>
            )}
          </div>

          <style>{`
            .ml-root {
              --ml-green: #25D366;
              --ml-green-dk: #128C7E;
              --ml-chat-bg: #efeae2;
              --ml-text-3: #8696a0;
              --ml-radius: 4px;
              flex: 1;
              overflow-y: auto;
              overflow-x: hidden;
              background: transparent;
              position: relative;
              scrollbar-width: thin;
              scrollbar-color: rgba(0,0,0,0.12) transparent;
              font-family: 'Segoe UI', system-ui, sans-serif;
              min-height: 0;
              min-width: 0;
            }
            .ml-root::-webkit-scrollbar { width: 4px; }
            .ml-root::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.12); border-radius: 4px; }

            .ml-inner {
              position: relative; z-index: 1;
              padding: 10px 11px;
              display: flex;
              flex-direction: column;
              gap: 6px;
              min-height: 100%;
              min-width: 0;
              max-width: 100%;
              overflow-x: hidden;
            }
            @media (min-width: 768px) {
              .ml-inner {
                padding: 14px 16px;
                gap: 4px;
              }
            }
            @media (max-width: 767px) {
              .ml-inner { padding-top: 14px; }
            }

            .ml-load-more {
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 8px;
              padding: 6px 0 10px;
              background: transparent;
            }
            .ml-load-more__label {
              font-size: 11px;
              font-weight: 600;
              color: var(--ml-text-3);
            }
            .ml-scroll-hint {
              text-align: center;
              font-size: 11px;
              color: var(--ml-text-3);
              padding: 4px 0 8px;
              margin: 0;
            }

            .ml-state-center { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px; padding: 60px 20px; }
            .ml-empty-icon { width: 60px; height: 60px; border-radius: var(--ml-radius); background: rgba(0,0,0,0.06); display: flex; align-items: center; justify-content: center; color: rgba(0,0,0,0.2); }
            .ml-empty-label { font-size: 14px; font-weight: 600; color: rgba(0,0,0,0.35); }
            .ml-empty-sub { font-size: 12px; color: var(--ml-text-3); text-align: center; }

            .ml-msg-wrapper { transition: outline 0.2s; border-radius: var(--ml-radius); outline: 2px solid transparent; min-width: 0; max-width: 100%; }
            .ml-msg-wrapper--highlight { outline-color: var(--ml-green); animation: ml-fade-outline 1.4s forwards; }
            @keyframes ml-fade-outline { 0% { outline-color: rgba(37,211,102,0.7); } 100% { outline-color: transparent; } }
          `}</style>
        </div>

        {showJumpToBottom && (
          <button
            type="button"
            className="ml-jump-bottom"
            onClick={() => scrollToBottom(true)}
            aria-label="Scroll to latest messages"
            title="Go to latest messages"
          >
            <ChevronDown size={20} strokeWidth={2.5} />
          </button>
        )}

        <style>{`
          .ml-root-wrap {
            position: relative;
            flex: 1;
            min-height: 0;
            min-width: 0;
            max-width: 100%;
            overflow: hidden;
            display: flex;
            flex-direction: column;
            background-color: #efeae2;
            background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23000' fill-opacity='0.035' fill-rule='evenodd'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/svg%3E");
            background-repeat: repeat;
          }
          .ml-jump-bottom {
            position: absolute;
            bottom: 16px;
            right: 16px;
            z-index: 20;
            width: 40px;
            height: 40px;
            border-radius: 9999px;
            border: 1px solid rgba(0,0,0,0.1);
            background: rgba(255,255,255,0.95);
            color: #111827;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 2px 10px rgba(0,0,0,0.12);
            cursor: pointer;
            transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
          }
          .ml-jump-bottom:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 14px rgba(0,0,0,0.16);
            background: #fff;
          }
          .ml-jump-bottom:active {
            transform: translateY(0);
          }
        `}</style>
      </div>
    );
  }
);

MessageList.displayName = "MessageList";

export default MessageList;
